import {
  createContext,
  useEffect,
  useState,
  useContext,
  ReactNode,
} from 'react'
import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously,
  User,
} from 'firebase/auth'
import { getFirestore, doc, getDoc } from 'firebase/firestore'

import { auth, db } from '../../shared/firebase/firebaseHelpers'
import {
  ensureAnonymousSignIn,
  getOrCreateUserRecord,
} from '../../shared/firebase/firebaseHelpers'
import { useAppDispatch } from '../../app/hooks'
import { setUserProfile } from './userSlice'
import { Loading } from '../../components/common'
import {
  AUTH_OFFLINE_ERROR_MSG,
  AUTH_TIMEOUT,
} from '../../shared/constants/global'

type AuthContextType = {
  user: User | null
  loading: boolean
  error: Error | null
  isOffline: boolean
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  isOffline: false,
})

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch()

  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [isOffline, setIsOffline] = useState(!navigator.onLine)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setError(new Error('Authentication timed out'))
      setLoading(false)
    }, AUTH_TIMEOUT)

    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        clearTimeout(timeout)

        try {
          let currentUser = firebaseUser

          if (!currentUser) {
            currentUser = await ensureAnonymousSignIn()
          }

          if (!currentUser) {
            throw new Error('No authenticated user')
          }

          setUser(currentUser)

          const userData = await getOrCreateUserRecord(currentUser)
          if (userData) {
            dispatch(setUserProfile(userData))
          }
        } catch (err) {
          setError(err as Error)
        } finally {
          setLoading(false)
        }
      },
      (err) => {
        clearTimeout(timeout)
        setError(err)
        setLoading(false)
      }
    )

    const handleOffline = () => {
      setIsOffline(true)
      setError(new Error(AUTH_OFFLINE_ERROR_MSG))
      setLoading(false)
    }

    const handleOnline = () => {
      setIsOffline(false)
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      clearTimeout(timeout)
      unsubscribe()
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, error, isOffline }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
